<script lang="ts" setup>
import { ref } from 'vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import CloseIcon from '@lahaus-roomie/roomie/src/assets/icons/close.svg'
import { useI18n } from 'vue-i18n'
import type { Props } from './types'
import { useAppChatStore } from '@/stores/appChatStore'

const props = withDefaults(defineProps<Props>(), {
  faceToFaceVisitEnabled: true,
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const appChatStore = useAppChatStore()
const { $useLhId } = useNuxtApp()
const { locale, t, tm, rt } = useI18n()

const showButtonWithChips = ref(false)
const chips = tm('chips')

// Determine if a chip should be shown based on its ID
const showChip = (id: string) => {
  const chipVisibilityRules = {
    brochure: () => props.listing.ims.has_brief,
    'inperson-schedule-visit': () => locale.value === 'mx' && props?.faceToFaceVisitEnabled !== false,
    default: () => true
  }

  return (chipVisibilityRules[id] || chipVisibilityRules.default)()
}

// Handle SAM button click
const handleSamButtonClick = () => {
  const endpointConfig = {
    ...appChatStore.endpointConfig,
    shouldCallBotForGreeting: true,
    startFlow: false
  }

  showButtonWithChips.value = false
  appChatStore.openChatWindow(endpointConfig)
}

// Handle chip click
const handleChipClick = (chip: any) => {
  const initialChatValues = {
    contactFormTitleCopy: rt(chip.form.title),
    contactFormCtaCopy: rt(chip.form.cta),
    userInitialMessage: rt(chip.message)
  }

  const greetingMessages = [
    {
      text: t('samGpt.greetingGeneral', {
        projectName: props.listing.name
      })
    }
  ]

  const endpointConfig = {
    ...appChatStore.endpointConfig,
    shouldCallBotForGreeting: false,
    startFlow: false
  }

  appChatStore.updateContactFormConfig(initialChatValues)
  appChatStore.openChatWindow(endpointConfig, greetingMessages)
}

defineExpose({
  showButtonWithChips
})
</script>

<script lang="ts">
export default {
  name: 'AppChatButton'
}
</script>

<template>
  <section class="app-chat-button-holder">
    <Transition name="scale">
      <div
        v-if="showButtonWithChips && !appChatStore.isChatWindowOpen"
        class="app-chat-button-with-chips">
        <div class="flex flex-col gap-8 items-end">
          <template
            v-for="(chip, index) in chips"
            :key="`app-chat-chip-selector-${rt(chip.id)}`">
            <RoomieChipSelector
              v-if="showChip(rt(chip.id))"
              :id="`app-chat-chip-selector-${rt(chip.id)}`"
              :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'expandir', C: rt(chip.label), I: index })"
              :is-selected="false"
              icon-placement="right"
              @click="handleChipClick(chip)">
              <template #content>
                {{ rt(chip.label) }}
              </template>

              <template #icon>
                <RoomieIcon
                  custom-styles="chips-icon"
                  :icon-name="rt(chip.icon)" />
              </template>
            </RoomieChipSelector>
          </template>
        </div>

        <button
          id="app-chat-close-tooltip-options"
          v-lh-id="{ ...dataAttributes.dataLh, A: 'seleccionar', C: t('closeOptions') }"
          :data-lh-id="`${screen}-app-chat-close-chip-options`"
          :aria-label="t('closeOptions')"
          type="button"
          class="circular-button bg-brand-800"
          @click="showButtonWithChips = false">
          <CloseIcon class="w-32 h-32 fill-white pointer-events-none" />
        </button>
      </div>
    </Transition>

    <div
      v-if="!showButtonWithChips || appChatStore.isChatWindowOpen"
      class="app-chat-agent-button">
      <button
        id="app-chat-expand-button"
        v-lh-id="{ ...dataAttributes.dataLh, A: 'seleccionar', C: 'cara sam'}"
        :aria-label="t('toggleChatCta')"
        type="button"
        class="circular-button relative"
        @click="handleSamButtonClick">
        <img
          :src="botAgentImage"
          :alt="botAgentName"
          class="w-full h-full object-cover pointer-events-none">
      </button>
    </div>
  </section>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss">
.lh-roomie-v2 {
  .app-chat-button-holder {
    .circular-button {
      @apply mt-6 rounded-full shadow-[0_0_10px_#0000004f] flex;
      @apply items-center justify-center border-2 border-spring-600;
      @apply overflow-hidden;

      width: 6.6rem;
      height: 6.6rem;
    }

    .app-chat-button-with-chips {
      @apply flex flex-col items-end;
    }

    .app-chat-agent-button {
      @apply relative flex flex-col items-end;
    }

    .roomie-chip-selector {
      @apply bg-carbon-800 border-carbon-800 text-white cursor-pointer;

      box-shadow: 0 0 5px 0 rgba(0 0 0 / 86%);
    }

    .chips-icon {
      @apply w-24 h-24 pointer-events-none;
    }
  }
}
</style>
